import React, { useState, useEffect } from 'react';
import AdminSidebar from '../components/Sidebar';
import { useSidebar } from '../context/SidebarContext';
import { db, storage } from '../config/firebase';
import {
    collection,
    onSnapshot,
    query,
    orderBy,
    doc,
    getDoc,
    addDoc,
    deleteDoc,
    updateDoc,
    getDocs,
    where
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const QUERY_STATUSES = {
    // Initial states
    PENDING: 'Pending',
    MORE_DETAILS_REQUESTED: 'More Details Requested',
    DETAILS_PROVIDED: 'Details Provided',
    ACCEPTED: 'Accepted',
    REJECTED: 'Rejected',

    // Driver workflow states
    DRIVER_ASSIGNED: 'Driver Assigned',
    DRIVER_ACCEPTED: 'Driver Accepted',
    DRIVER_ON_WAY: 'Driver On Way',
    DRIVER_REACHED: 'Driver Reached',
    DRIVER_VERIFIED: 'Site Verified',
    DRIVER_REPORTED: 'Inspection Completed',

    // Quotation states
    QUOTATION_UPLOADED: 'Quotation Uploaded',
    QUOTATION_ACCEPTED: 'Quotation Accepted',
    QUOTATION_REJECTED: 'Quotation Rejected',

    // Technician workflow states
    TECHNICIAN_ASSIGNED: 'Technician Assigned',
    TECHNICIAN_ACCEPTED: 'Technician Accepted',
    TECHNICIAN_ON_WAY: 'Technician On Way',
    TECHNICIAN_REACHED: 'Technician Reached',
    TECHNICIAN_WORKING: 'Work in Progress',
    TECHNICIAN_COMPLETED: 'Work Completed',

    // Invoice and payment states
    INVOICE_GENERATED: 'Invoice Generated',
    PAYMENT_PENDING: 'Payment Pending',
    PAYMENT_UPLOADED: 'Payment Uploaded',
    PAID: 'Paid',

    // Follow-up states
    FOLLOW_UP_SCHEDULED: 'Follow-up Scheduled',
    FOLLOW_UP_COMPLETED: 'Follow-up Completed',
    CLOSED: 'Closed'
};

const getWorkflowProgress = (status) => {
    const progressMap = {
        [QUERY_STATUSES.PENDING]: 5,
        [QUERY_STATUSES.MORE_DETAILS_REQUESTED]: 10,
        [QUERY_STATUSES.DETAILS_PROVIDED]: 15,
        [QUERY_STATUSES.ACCEPTED]: 20,
        [QUERY_STATUSES.DRIVER_ASSIGNED]: 25,
        [QUERY_STATUSES.DRIVER_ACCEPTED]: 30,
        [QUERY_STATUSES.DRIVER_ON_WAY]: 35,
        [QUERY_STATUSES.DRIVER_REACHED]: 40,
        [QUERY_STATUSES.DRIVER_VERIFIED]: 45,
        [QUERY_STATUSES.DRIVER_REPORTED]: 50,
        [QUERY_STATUSES.QUOTATION_UPLOADED]: 55,
        [QUERY_STATUSES.QUOTATION_ACCEPTED]: 60,
        [QUERY_STATUSES.TECHNICIAN_ASSIGNED]: 65,
        [QUERY_STATUSES.TECHNICIAN_ACCEPTED]: 70,
        [QUERY_STATUSES.TECHNICIAN_ON_WAY]: 75,
        [QUERY_STATUSES.TECHNICIAN_REACHED]: 80,
        [QUERY_STATUSES.TECHNICIAN_WORKING]: 85,
        [QUERY_STATUSES.TECHNICIAN_COMPLETED]: 90,
        [QUERY_STATUSES.INVOICE_GENERATED]: 95,
        [QUERY_STATUSES.PAYMENT_UPLOADED]: 98,
        [QUERY_STATUSES.PAID]: 100,
        [QUERY_STATUSES.FOLLOW_UP_COMPLETED]: 100,
        [QUERY_STATUSES.CLOSED]: 100
    };
    return progressMap[status] || 0;
};


function Queries() {
    const [queries, setQueries] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const { isMobile, isOpen, toggleMobileSidebar } = useSidebar();
    const [isLoading, setIsLoading] = useState(true);

    // Context & Data
    const [partnerNames, setPartnerNames] = useState({});
    const [drivers, setDrivers] = useState([]);
    const [technicians, setTechnicians] = useState([]);
    const [allPartners, setAllPartners] = useState([]);
    const [managers, setManagers] = useState([]);
    const [selectedQuery, setSelectedQuery] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [invoices, setInvoices] = useState([]);

    // Editing States
    const [isEditingTime, setIsEditingTime] = useState(false);
    const [editTimeValue, setEditTimeValue] = useState('');
    const [uploadingType, setUploadingType] = useState(null); // 'invoice', 'inspection', 'completion', 'quotation'
    const [invoiceAmount, setInvoiceAmount] = useState('');
    const [editInvoiceAmount, setEditInvoiceAmount] = useState('');
    const [isEditingInvoiceAmount, setIsEditingInvoiceAmount] = useState(false);
    const [isChangingDriver, setIsChangingDriver] = useState(false);
    const [isChangingTechnician, setIsChangingTechnician] = useState(false);
    const [isChangingPartner, setIsChangingPartner] = useState(false);
    const [adminReply, setAdminReply] = useState('');
    const [isReplying, setIsReplying] = useState(false);
    const [quotationAmount, setQuotationAmount] = useState('');

    // Fetch Queries and Employees
    useEffect(() => {
        const q = query(collection(db, 'queries'), orderBy('createdAt', 'desc'));
        onSnapshot(q, async (snapshot) => {
            const queriesData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setQueries(queriesData);

            // Fetch Partner Names
            const uniquePartnerIds = [...new Set(queriesData.map(q => q.partnerId).filter(Boolean))];
            const names = {};
            await Promise.all(uniquePartnerIds.map(async (pid) => {
                if (!partnerNames[pid]) {
                    try {
                        const pDoc = await getDoc(doc(db, 'partners', pid));
                        if (pDoc.exists()) {
                            names[pid] = pDoc.data().company || pDoc.data().name;
                        }
                    } catch (e) {
                        console.error("Error fetching partner:", e);
                    }
                }
            }));
            setPartnerNames(prev => ({ ...prev, ...names }));
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching queries:", error);
            setIsLoading(false);
        });

        // Fetch Employees (Drivers, Technicians, Managers) & Partners
        // Fetch Employees (Drivers, Technicians, Managers) & Partners
        const fetchStaff = async () => {
            try {
                const employeesSnapshot = await getDocs(query(collection(db, 'employees')));
                const employeesData = employeesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                setDrivers(employeesData.filter(e => e.role === 'driver'));
                setTechnicians(employeesData.filter(e => e.role === 'technician'));
                setManagers(employeesData.filter(e => e.role === 'manager'));

                // Also fetch users who might be managers (as fallback/completeness)
                const usersSnapshot = await getDocs(query(collection(db, 'users'), where('role', '==', 'manager')));
                const usersManagers = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                // Combine and deduplicate managers
                setManagers(prev => {
                    const combined = [...prev, ...usersManagers];
                    const unique = Array.from(new Map(combined.map(m => [m.id, m])).values());
                    return unique;
                });

                const partnersSnapshot = await getDocs(query(collection(db, 'partners')));
                const partnersData = partnersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setAllPartners(partnersData);
            } catch (error) {
                console.error("Error fetching staff:", error);
            }
        };

        fetchStaff();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch Invoices
    useEffect(() => {
        if (selectedQuery) {
            const invoiceQ = query(collection(db, `queries/${selectedQuery.id}/invoices`), orderBy('createdAt', 'desc'));
            const unsub = onSnapshot(invoiceQ, (snap) => {
                setInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            });
            return () => unsub();
        } else {
            setInvoices([]);
        }
    }, [selectedQuery]);

    // Sync quotationAmount state when selectedQuery changes
    useEffect(() => {
        if (selectedQuery) {
            setQuotationAmount(selectedQuery.quotationAmount || '');
        } else {
            setQuotationAmount('');
        }
    }, [selectedQuery]);

    // Sorting Helper
    const filteredQueries = queries.filter(q => {
        const pName = partnerNames[q.partnerId] || '';
        const matchesSearch = (q.serviceType?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (q.description?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            pName.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || q.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    const getStatusColor = (status) => {
        if (!status) return 'bg-gray-100 text-gray-800';
        const formatted = status.toLowerCase();
        if (formatted.includes('pending')) return 'bg-yellow-100 text-yellow-800';
        if (formatted.includes('completed') || formatted.includes('success') || formatted.includes('paid')) return 'bg-green-100 text-green-800';
        if (formatted.includes('progress') || formatted.includes('way') || formatted.includes('assigned')) return 'bg-blue-100 text-blue-800';
        if (formatted.includes('reject') || formatted.includes('cancel')) return 'bg-red-100 text-red-800';
        return 'bg-gray-100 text-gray-800';
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        if (timestamp.seconds) return new Date(timestamp.seconds * 1000).toLocaleDateString();
        return new Date(timestamp).toLocaleDateString();
    };

    // Actions
    const handleAdminUploadPaymentProof = async (e) => {
        if (!e.target.files?.length || !selectedQuery) return;
        setUploadingType('payment');
        try {
            const file = e.target.files[0];
            const url = await uploadFileToStorage(file, 'payment-proofs');

            await updateDoc(doc(db, 'queries', selectedQuery.id), {
                paymentProof: url,
                status: QUERY_STATUSES.PAYMENT_UPLOADED
            });

            setSelectedQuery(prev => ({
                ...prev,
                paymentProof: url,
                status: QUERY_STATUSES.PAYMENT_UPLOADED
            }));
        } catch (error) {
            console.error("Error uploading payment proof:", error);
            alert("Failed to upload payment proof");
        } finally {
            setUploadingType(null);
            e.target.value = null;
        }
    };

    const handleVerifyPayment = async () => {
        if (!selectedQuery) return;
        if (!window.confirm("Are you sure you want to accept this payment?")) return;
        setIsUpdating(true);
        try {
            await updateDoc(doc(db, 'queries', selectedQuery.id), {
                status: QUERY_STATUSES.PAID
            });
            setSelectedQuery(prev => ({ ...prev, status: QUERY_STATUSES.PAID }));
        } catch (error) {
            console.error("Error verifying payment:", error);
            alert("Failed to verify payment");
        } finally {
            setIsUpdating(false);
        }
    };

    const handleRejectPayment = async () => {
        if (!selectedQuery) return;
        if (!window.confirm("Are you sure you want to reject this payment?")) return;
        setIsUpdating(true);
        try {
            await updateDoc(doc(db, 'queries', selectedQuery.id), {
                status: QUERY_STATUSES.INVOICE_GENERATED,
                paymentProof: null
            });
            setSelectedQuery(prev => ({
                ...prev,
                status: QUERY_STATUSES.INVOICE_GENERATED,
                paymentProof: null
            }));
        } catch (error) {
            console.error("Error rejecting payment:", error);
            alert("Failed to reject payment");
        } finally {
            setIsUpdating(false);
        }
    };

    const handleUpdateStatus = async (newStatus) => {
        if (!selectedQuery) return;
        try {
            await updateDoc(doc(db, 'queries', selectedQuery.id), { status: newStatus });
            setSelectedQuery(prev => ({ ...prev, status: newStatus }));
        } catch (error) {
            console.error("Error updating status:", error);
            alert("Failed to update status");
        }
    };

    const handleAssignDriver = async (driverId) => {
        if (!selectedQuery) return;
        setIsUpdating(true);
        try {
            const driver = drivers.find(d => d.id === driverId);
            await updateDoc(doc(db, 'queries', selectedQuery.id), {
                driverId: driverId,
                driverName: driver ? driver.name : '',
                driverPhone: driver ? driver.phone : '',
                status: QUERY_STATUSES.DRIVER_ASSIGNED
            });
            setSelectedQuery(prev => ({ ...prev, driverId: driverId, driverName: driver ? driver.name : '', driverPhone: driver ? driver.phone : '', status: QUERY_STATUSES.DRIVER_ASSIGNED }));
        } catch (error) {
            console.error("Error assigning driver:", error);
            alert("Failed to assign driver");
        } finally {
            setIsUpdating(false);
        }
    };

    const handleAssignTechnician = async (technicianId) => {
        if (!selectedQuery) return;
        setIsUpdating(true);
        try {
            const tech = technicians.find(t => t.id === technicianId);
            await updateDoc(doc(db, 'queries', selectedQuery.id), {
                technicianId: technicianId,
                technicianName: tech ? tech.name : '',
                status: QUERY_STATUSES.TECHNICIAN_ASSIGNED
            });
            setSelectedQuery(prev => ({ ...prev, technicianId: technicianId, technicianName: tech ? tech.name : '', status: QUERY_STATUSES.TECHNICIAN_ASSIGNED }));
        } catch (error) {
            console.error("Error assigning technician:", error);
            alert("Failed to assign technician");
        } finally {
            setIsUpdating(false);
        }
    };

    const handleUpdatePartnerAndManager = async (partnerId, managerId) => {
        if (!selectedQuery) return;
        setIsUpdating(true);
        try {
            const partner = allPartners.find(p => p.id === partnerId);

            const updates = {
                partnerId: partnerId,
                partnerName: partner ? (partner.company || partner.name) : '',
            };

            // If a manager is selected, we might want to store their info
            if (managerId) {
                updates.createdRole = 'manager';
                updates.createdBy = managerId;
            } else {
                updates.createdRole = 'partner'; // Default back to partner
                updates.createdBy = partnerId;
            }

            await updateDoc(doc(db, 'queries', selectedQuery.id), updates);

            setSelectedQuery(prev => ({
                ...prev,
                ...updates,
                partnerName: partner ? (partner.company || partner.name) : prev.partnerName
            }));

            // Update partnerNames cache
            if (partner) {
                setPartnerNames(prev => ({ ...prev, [partnerId]: partner.company || partner.name }));
            }

            setIsChangingPartner(false);
        } catch (error) {
            console.error("Error updating partner/manager:", error);
            alert("Failed to update partner/manager");
        } finally {
            setIsUpdating(false);
        }
    };

    const saveTime = async () => {
        if (!selectedQuery) return;
        try {
            await updateDoc(doc(db, 'queries', selectedQuery.id), { preferredTime: editTimeValue });
            setSelectedQuery(prev => ({ ...prev, preferredTime: editTimeValue }));
            setIsEditingTime(false);
        } catch (error) {
            console.error("Error updating time:", error);
            alert("Failed to update time");
        }
    };



    // File Uploads
    const uploadFileToStorage = async (file, pathValues) => {
        const storageRef = ref(storage, `${pathValues}/${selectedQuery.id}/${Date.now()}_${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        return await getDownloadURL(snapshot.ref);
    };

    const handleUploadReportImage = async (e, type) => {
        if (!e.target.files?.length || !selectedQuery) return;
        setUploadingType(type);
        // Admin uploads go to separate admin fields so partner sees only admin content
        const fieldName = type === 'inspection' ? 'adminInspectionImages' : 'adminCompletionImages';
        const path = type === 'inspection' ? 'inspection_images' : 'completion_images';

        try {
            const newUrls = [];
            for (const file of e.target.files) {
                const url = await uploadFileToStorage(file, path);
                newUrls.push(url);
            }
            const currentImages = selectedQuery[fieldName] || [];
            const updatedImages = [...currentImages, ...newUrls];
            await updateDoc(doc(db, 'queries', selectedQuery.id), { [fieldName]: updatedImages });
            setSelectedQuery(prev => ({ ...prev, [fieldName]: updatedImages }));
        } catch (error) {
            console.error(`Error uploading ${type} image:`, error);
            alert("Failed to upload image");
        } finally {
            setUploadingType(null);
            e.target.value = null;
        }
    };

    const handleDeleteReportImage = async (url, type) => {
        if (!window.confirm("Delete this image?")) return;
        // Admin uploads go to separate admin fields
        const fieldName = type === 'inspection' ? 'adminInspectionImages' : 'adminCompletionImages';
        try {
            const currentImages = selectedQuery[fieldName] || [];
            const updatedImages = currentImages.filter(img => (typeof img === 'string' ? img : img.url || img.uri) !== url);
            await updateDoc(doc(db, 'queries', selectedQuery.id), { [fieldName]: updatedImages });
            setSelectedQuery(prev => ({ ...prev, [fieldName]: updatedImages }));
        } catch (error) {
            console.error("Error deleting image:", error);
        }
    };

    const handleUploadQuotation = async (e) => {
        if (!e.target.files?.length || !selectedQuery) return;
        setUploadingType('quotation');
        try {
            const file = e.target.files[0];
            const url = await uploadFileToStorage(file, 'quotations');
            const amount = quotationAmount !== '' ? quotationAmount : (selectedQuery.quotationAmount || null);
            await updateDoc(doc(db, 'queries', selectedQuery.id), {
                quotationUrl: url,
                quotationUploadedAt: new Date(),
                quotationAmount: amount,
                status: QUERY_STATUSES.QUOTATION_UPLOADED
            });
            setSelectedQuery(prev => ({
                ...prev,
                quotationUrl: url,
                quotationUploadedAt: new Date(),
                quotationAmount: amount,
                status: QUERY_STATUSES.QUOTATION_UPLOADED
            }));
        } catch (error) {
            console.error("Error uploading quotation:", error);
            alert("Failed to upload quotation");
        } finally {
            setUploadingType(null);
            e.target.value = null;
        }
    };

    const handleUpdateQuotationAmount = async () => {
        if (!selectedQuery) return;
        const amount = quotationAmount !== '' ? quotationAmount : (selectedQuery.quotationAmount || null);
        try {
            await updateDoc(doc(db, 'queries', selectedQuery.id), {
                quotationAmount: amount
            });
            setSelectedQuery(prev => ({
                ...prev,
                quotationAmount: amount
            }));
        } catch (error) {
            console.error("Error updating quotation amount:", error);
            alert("Failed to update amount");
        }
    };

    const handleUpdateInvoiceAmount = async () => {
        if (!selectedQuery) return;
        const amount = parseFloat(editInvoiceAmount) || null;
        try {
            await updateDoc(doc(db, 'queries', selectedQuery.id), { amount });
            setSelectedQuery(prev => ({ ...prev, amount }));
            setEditInvoiceAmount('');
            setIsEditingInvoiceAmount(false);
        } catch (error) {
            console.error("Error updating invoice amount:", error);
            alert("Failed to update invoice amount");
        }
    };

    const handleDeleteQuotation = async () => {
        if (!window.confirm("Delete quotation? This will revert status.")) return;
        try {
            await updateDoc(doc(db, 'queries', selectedQuery.id), {
                quotationUrl: null,
                quotationUploadedAt: null,
                quotationAmount: null,
                status: QUERY_STATUSES.DRIVER_REPORTED
            });
            setSelectedQuery(prev => ({
                ...prev,
                quotationUrl: null,
                quotationUploadedAt: null,
                quotationAmount: null,
                status: QUERY_STATUSES.DRIVER_REPORTED
            }));
        } catch (error) {
            console.error("Error deleting quotation:", error);
        }
    };

    const handleReplyFollowUp = async () => {
        if (!selectedQuery || !adminReply.trim()) return;
        setIsUpdating(true);
        try {
            const updatedFollowUp = {
                ...selectedQuery.followUp,
                adminReply: adminReply,
                repliedBy: 'admin', // Or current admin ID if available
                replyAt: new Date()
            };

            await updateDoc(doc(db, 'queries', selectedQuery.id), {
                followUp: updatedFollowUp
            });

            setSelectedQuery(prev => ({
                ...prev,
                followUp: updatedFollowUp
            }));
            setIsReplying(false);
            setAdminReply('');
        } catch (error) {
            console.error("Error replying to follow-up:", error);
            alert("Failed to send reply");
        } finally {
            setIsUpdating(false);
        }
    };

    const handleResolveFollowUp = async () => {
        if (!selectedQuery) return;
        if (!window.confirm("Mark this issue as resolved?")) return;
        setIsUpdating(true);
        try {
            const updatedFollowUp = {
                ...selectedQuery.followUp,
                status: 'resolved',
                resolvedAt: new Date(),
                resolvedBy: 'admin'
            };

            await updateDoc(doc(db, 'queries', selectedQuery.id), {
                followUp: updatedFollowUp
            });

            setSelectedQuery(prev => ({
                ...prev,
                followUp: updatedFollowUp
            }));
        } catch (error) {
            console.error("Error resolving follow-up:", error);
            alert("Failed to resolve issue");
        } finally {
            setIsUpdating(false);
        }
    };

    const handleUploadInvoice = async (e) => {
        if (!e.target.files?.length || !selectedQuery) return;
        setUploadingType('invoice');
        try {
            const amount = parseFloat(invoiceAmount) || 0;
            let lastInvoiceId = null;
            for (const file of e.target.files) {
                const url = await uploadFileToStorage(file, 'invoices');
                const invRef = await addDoc(collection(db, `queries/${selectedQuery.id}/invoices`), {
                    fileUrl: url,
                    fileName: file.name,
                    createdAt: new Date(),
                    invoiceNumber: `INV-${Date.now()}`,
                    totalAmount: amount
                });
                lastInvoiceId = invRef.id;
            }
            if (lastInvoiceId) {
                await updateDoc(doc(db, 'queries', selectedQuery.id), {
                    status: QUERY_STATUSES.INVOICE_GENERATED,
                    invoiceId: lastInvoiceId,
                    amount: amount
                });
                setSelectedQuery(prev => ({ ...prev, status: QUERY_STATUSES.INVOICE_GENERATED, invoiceId: lastInvoiceId, amount }));
            }
            setInvoiceAmount('');
        } catch (error) {
            console.error("Error uploading invoice:", error);
            alert("Failed to upload invoice");
        } finally {
            setUploadingType(null);
            e.target.value = null;
        }
    };

    const handleDeleteInvoice = async (invoiceId) => {
        if (!window.confirm("Delete this invoice?")) return;
        try {
            await deleteDoc(doc(db, `queries/${selectedQuery.id}/invoices`, invoiceId));
        } catch (error) {
            console.error("Error deleting invoice:", error);
        }
    };

    const getImageUri = (img) => {
        if (!img) return null;
        return typeof img === 'string' ? img : (img.uri || img.url);
    };

    return (
        <div className="flex h-screen overflow-hidden bg-gray-50">
            <AdminSidebar />
            <div
                className="flex-1 overflow-y-auto transition-all duration-300"
                style={{ marginLeft: isMobile ? 0 : (isOpen ? '16rem' : '5rem') }}
            >
                <div className="p-8">
                    <div className="flex items-center mb-2">
                        {isMobile && (
                            <button
                                onClick={toggleMobileSidebar}
                                className="mr-4 p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-brand-golden"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                            </button>
                        )}
                        <h1 className="text-3xl font-bold text-gray-800">Queries</h1>
                    </div>

                    {/* Filters */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                        <div className="flex bg-gray-100 p-1 rounded-lg overflow-x-auto">
                            {['all', 'Pending', 'Accepted', 'Completed', 'Paid'].map(status => (
                                <button
                                    key={status}
                                    onClick={() => setFilterStatus(status)}
                                    className={`px-4 py-2 rounded-md text-sm font-medium capitalize transition-colors ${filterStatus === status ? 'bg-white shadow text-black' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    {status}
                                </button>
                            ))}
                        </div>
                        <div className="relative w-full sm:w-64">
                            <input
                                type="text"
                                placeholder="Search queries..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-golden"
                            />
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Partner</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Service</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Location</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Date</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredQueries.map((query) => (
                                    <tr key={query.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-gray-900">{partnerNames[query.partnerId] || 'Unknown'}</div>
                                            {query.createdRole === 'manager' && <span className="text-xs text-gray-400">Manager</span>}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">{query.serviceType}</td>
                                        <td className="px-6 py-4 text-gray-500 text-sm max-w-xs truncate">{query.location}</td>
                                        <td className="px-6 py-4 text-gray-500 text-sm">{formatDate(query.createdAt)}</td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(query.status)}`}>
                                                {query.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <button onClick={() => setSelectedQuery(query)} className="text-blue-600 hover:text-blue-800 text-sm font-semibold">View</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredQueries.length === 0 && !isLoading && (
                            <div className="p-8 text-center text-gray-500">No queries found.</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal - Aligned with Mobile App UI */}
            {selectedQuery && (
                <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto flex flex-col shadow-2xl relative">

                        {/* Header */}
                        <div className="p-6 pb-2">
                            <div className="flex justify-between items-start mb-3">
                                <div className="flex-1 pr-4">
                                    <h2 className="text-2xl font-bold text-gray-900">{selectedQuery.serviceType || selectedQuery.title}</h2>
                                    <p className="text-xs text-gray-400 mt-1">Query ID: {selectedQuery.id}</p>
                                </div>
                                <button
                                    onClick={() => setSelectedQuery(null)}
                                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(selectedQuery.status)}`}>
                                {selectedQuery.status}
                            </span>
                        </div>

                        <div className="p-6 pt-2 space-y-6">

                            {/* Created By & Info */}
                            <div className="flex justify-between text-sm">
                                <div>
                                    <p className="text-gray-500 text-xs font-bold uppercase">Created By</p>
                                    {!isChangingPartner ? (
                                        <div className="flex items-center gap-2">
                                            <p className="font-semibold text-gray-900">
                                                {partnerNames[selectedQuery.partnerId] || selectedQuery.partnerName}
                                                {selectedQuery.createdRole === 'manager' && ' (Manager)'}
                                            </p>
                                            <button
                                                onClick={() => setIsChangingPartner(true)}
                                                className="text-xs font-bold text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-0.5 rounded transition-colors"
                                            >
                                                Change
                                            </button>
                                        </div>
                                    ) : (
                                        <PartnerManagerEditor
                                            partners={allPartners}
                                            managers={managers}
                                            currentPartnerId={selectedQuery.partnerId}
                                            currentManagerId={selectedQuery.createdRole === 'manager' ? selectedQuery.createdBy : ''}
                                            onSave={handleUpdatePartnerAndManager}
                                            onCancel={() => setIsChangingPartner(false)}
                                        />
                                    )}
                                </div>
                                <div className="text-right">
                                    <p className="text-gray-500 text-xs font-bold uppercase">Start Date</p>
                                    <p className="font-medium text-gray-900">{formatDate(selectedQuery.createdAt)}</p>
                                </div>
                            </div>

                            {/* Preferred Time */}
                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <p className="text-gray-500 text-xs font-bold uppercase">Preferred Time</p>
                                    {!isEditingTime && (
                                        <button onClick={() => { setIsEditingTime(true); setEditTimeValue(selectedQuery.preferredTime || ''); }} className="text-yellow-600 text-xs font-bold hover:underline">Edit</button>
                                    )}
                                </div>
                                {isEditingTime ? (
                                    <div className="flex gap-2">
                                        <input type="time" value={editTimeValue} onChange={(e) => setEditTimeValue(e.target.value)} className="border p-1 rounded text-sm" />
                                        <button onClick={saveTime} className="text-green-600 text-xs font-bold">Save</button>
                                        <button onClick={() => setIsEditingTime(false)} className="text-gray-500 text-xs">Cancel</button>
                                    </div>
                                ) : (
                                    <p className="font-medium text-gray-900">{selectedQuery.preferredTime || 'Not Set'}</p>
                                )}
                            </div>

                            {/* Location & Map */}
                            <div>
                                <p className="text-gray-500 text-xs font-bold uppercase mb-1">Location</p>
                                <p className="text-sm font-medium text-gray-900 mb-2">{selectedQuery.location}</p>
                                {selectedQuery.latitude ? (
                                    <div className="w-full h-40 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                                        <iframe
                                            width="100%"
                                            height="100%"
                                            frameBorder="0"
                                            scrolling="no"
                                            marginHeight="0"
                                            marginWidth="0"
                                            src={`https://maps.google.com/maps?q=${selectedQuery.latitude},${selectedQuery.longitude}&z=15&output=embed`}
                                            title="Map"
                                        ></iframe>
                                    </div>
                                ) : (
                                    <div className="w-full h-24 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-xs border border-dashed border-gray-300">
                                        No Coordinates
                                    </div>
                                )}
                            </div>

                            {/* Pending State - Accept/Reject */}
                            {selectedQuery.status === QUERY_STATUSES.PENDING && (
                                <div className="flex gap-3 pt-2">
                                    <button
                                        onClick={() => handleUpdateStatus(QUERY_STATUSES.REJECTED)}
                                        className="flex-1 bg-red-50 text-red-600 py-3 rounded-lg font-bold hover:bg-red-100 transition-colors"
                                    >
                                        Reject
                                    </button>
                                    <button
                                        onClick={() => handleUpdateStatus(QUERY_STATUSES.ACCEPTED)}
                                        className="flex-1 bg-green-50 text-green-600 py-3 rounded-lg font-bold hover:bg-green-100 transition-colors"
                                    >
                                        Accept
                                    </button>
                                </div>
                            )}

                            {/* Workflow Sections */}

                            {/* Driver Assignment */}
                            {getWorkflowProgress(selectedQuery.status) >= getWorkflowProgress(QUERY_STATUSES.ACCEPTED) && (
                                <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                                    <div className="flex items-center gap-2 mb-2 text-blue-900 font-bold text-sm">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                        Assign To Driver
                                    </div>
                                    {selectedQuery.driverId && !isChangingDriver ? (
                                        <div className="flex justify-between items-center bg-white p-3 rounded-lg border border-blue-200">
                                            <div className="flex items-center">
                                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mr-3">
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-gray-900">{selectedQuery.driverName}</p>
                                                    <p className="text-xs text-blue-600 font-medium">Driver Assigned</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => setIsChangingDriver(true)}
                                                className="text-xs font-bold text-blue-600 hover:text-blue-800 bg-blue-50 px-3 py-1.5 rounded-md hover:bg-blue-100 transition-colors"
                                            >
                                                Change
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <select
                                                className="w-full p-2.5 bg-white border border-blue-200 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-50"
                                                value={selectedQuery.driverId || ''}
                                                onChange={(e) => {
                                                    handleAssignDriver(e.target.value);
                                                    setIsChangingDriver(false);
                                                }}
                                                disabled={isUpdating}
                                            >
                                                <option value="">Select Driver</option>
                                                {drivers.map(d => (
                                                    <option key={d.id} value={d.id}>{d.name} {d.status === 'active' ? '(Active)' : ''}</option>
                                                ))}
                                            </select>
                                            {isChangingDriver && (
                                                <button onClick={() => setIsChangingDriver(false)} className="mt-2 text-xs text-gray-500 hover:text-gray-700 underline">Cancel</button>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Technician Assignment */}
                            {getWorkflowProgress(selectedQuery.status) >= getWorkflowProgress(QUERY_STATUSES.QUOTATION_ACCEPTED) && (
                                <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
                                    <div className="flex items-center gap-2 mb-2 text-purple-900 font-bold text-sm">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                        Assign Technician
                                    </div>
                                    {selectedQuery.technicianId && !isChangingTechnician ? (
                                        <div className="flex justify-between items-center bg-white p-3 rounded-lg border border-purple-200">
                                            <div className="flex items-center">
                                                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 mr-3">
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-gray-900">{selectedQuery.technicianName}</p>
                                                    <p className="text-xs text-purple-600 font-medium">Technician Assigned</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => setIsChangingTechnician(true)}
                                                className="text-xs font-bold text-purple-600 hover:text-purple-800 bg-purple-50 px-3 py-1.5 rounded-md hover:bg-purple-100 transition-colors"
                                            >
                                                Change
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <select
                                                className="w-full p-2.5 bg-white border border-purple-200 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-purple-500 outline-none disabled:opacity-50"
                                                value={selectedQuery.technicianId || ''}
                                                onChange={(e) => {
                                                    handleAssignTechnician(e.target.value);
                                                    setIsChangingTechnician(false);
                                                }}
                                                disabled={isUpdating}
                                            >
                                                <option value="">Select Technician</option>
                                                {technicians.map(t => (
                                                    <option key={t.id} value={t.id}>{t.name} {t.status === 'active' ? '(Active)' : ''}</option>
                                                ))}
                                            </select>
                                            {isChangingTechnician && (
                                                <button onClick={() => setIsChangingTechnician(false)} className="mt-2 text-xs text-gray-500 hover:text-gray-700 underline">Cancel</button>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Inspection Reports */}
                            {getWorkflowProgress(selectedQuery.status) >= getWorkflowProgress(QUERY_STATUSES.DRIVER_REPORTED) && (
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <h3 className="text-sm font-bold text-gray-900">Inspection Reports</h3>
                                        <label className="bg-gray-100 text-gray-600 hover:bg-gray-200 px-3 py-1 rounded-md text-xs font-semibold cursor-pointer">
                                            {uploadingType === 'inspection' ? 'Uploading...' : 'Add Photos / PDF'}
                                            <input type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={(e) => handleUploadReportImage(e, 'inspection')} disabled={uploadingType} />
                                        </label>
                                    </div>
                                    {selectedQuery.driverReport && (
                                        <div className="bg-gray-50 p-3 rounded-lg mb-2 text-sm text-gray-700 italic border border-gray-100">
                                            "{selectedQuery.driverReport}"
                                        </div>
                                    )}
                                    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                                        {[...(selectedQuery.inspectionImages || []), ...(selectedQuery.adminInspectionImages || [])].map((img, idx) => {
                                            const url = getImageUri(img);
                                            const isPdf = url && url.toLowerCase().endsWith('.pdf');
                                            return url ? (
                                                <div key={idx} className="relative w-24 h-24 flex-shrink-0 group">
                                                    {isPdf ? (
                                                        <a href={url} target="_blank" rel="noreferrer" className="w-full h-full flex flex-col items-center justify-center bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
                                                            <svg className="w-6 h-6 text-red-500 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                                            <span className="text-xs text-red-600 font-medium">PDF</span>
                                                        </a>
                                                    ) : (
                                                        <a href={url} target="_blank" rel="noreferrer">
                                                            <img src={url} className="w-full h-full object-cover rounded-lg border border-gray-200" alt="Inspection" />
                                                        </a>
                                                    )}
                                                    <button onClick={() => handleDeleteReportImage(url, 'inspection')} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity transform hover:scale-110">
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                                    </button>
                                                </div>
                                            ) : null;
                                        })}
                                        {(!selectedQuery.inspectionImages?.length && !selectedQuery.adminInspectionImages?.length) && <p className="text-xs text-gray-400 py-2">No files available</p>}
                                    </div>
                                </div>
                            )}

                            {/* Quotation Upload */}
                            {getWorkflowProgress(selectedQuery.status) >= getWorkflowProgress(QUERY_STATUSES.DRIVER_REPORTED) && (
                                <div className="pt-4 border-t border-gray-100">
                                    <div className="flex justify-between items-center mb-2">
                                        <h3 className="text-sm font-bold text-gray-900">Quotation</h3>
                                        {selectedQuery.status === QUERY_STATUSES.QUOTATION_ACCEPTED && <span className="text-green-600 text-xs font-bold bg-green-50 px-2 py-1 rounded">Accepted</span>}
                                    </div>

                                    {selectedQuery.quotationUrl ? (
                                        <div>
                                            <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-lg">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center text-blue-600"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg></div>
                                                    <div>
                                                        <p className="text-sm font-bold text-blue-900">Quotation.pdf</p>
                                                        <p className="text-xs text-blue-700">{formatDate(selectedQuery.quotationUploadedAt)}</p>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2">
                                                    <a href={selectedQuery.quotationUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800 p-1"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg></a>
                                                    <button onClick={handleDeleteQuotation} className="text-red-500 hover:text-red-700 p-1"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3 mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                                <span className="text-sm font-semibold text-gray-700">Quotation Amount</span>
                                                <span className="text-lg font-bold text-gray-900">{selectedQuery.quotationAmount ? parseFloat(selectedQuery.quotationAmount).toFixed(2) : '0.00'} <span className="text-xs text-gray-500 font-normal">AED</span></span>
                                                <div className="ml-auto flex items-center gap-2">
                                                    <input type="number" step="0.01" min="0" className="w-24 px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500" value={quotationAmount} onChange={(e) => setQuotationAmount(e.target.value)} placeholder="0.00" />
                                                    <button onClick={handleUpdateQuotationAmount} className="bg-yellow-500 text-black hover:bg-yellow-400 px-3 py-1 rounded-md text-xs font-bold">Update</button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                                                <p className="text-sm font-medium text-gray-600">{uploadingType === 'quotation' ? 'Uploading...' : 'Upload Quotation PDF'}</p>
                                                <input type="file" accept="application/pdf,image/*" className="hidden" onChange={handleUploadQuotation} disabled={uploadingType} />
                                            </label>
                                            <div className="flex items-center gap-3 mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                                <span className="text-sm font-semibold text-gray-700">Amount</span>
                                                <div className="flex items-center gap-1">
                                                    <input type="number" step="0.01" min="0" className="w-28 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500" value={quotationAmount} onChange={(e) => setQuotationAmount(e.target.value)} placeholder="0.00" />
                                                    <span className="text-sm text-gray-500">AED</span>
                                                </div>
                                                <button onClick={handleUpdateQuotationAmount} className="bg-yellow-500 text-black hover:bg-yellow-400 px-3 py-1 rounded-md text-xs font-bold ml-auto">Save Amount</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Completion Reports */}
                            {getWorkflowProgress(selectedQuery.status) >= getWorkflowProgress(QUERY_STATUSES.TECHNICIAN_COMPLETED) && (
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <h3 className="text-sm font-bold text-gray-900">Completion Reports</h3>
                                        <label className="bg-gray-100 text-gray-600 hover:bg-gray-200 px-3 py-1 rounded-md text-xs font-semibold cursor-pointer">
                                            {uploadingType === 'completion' ? 'Uploading...' : 'Add Photos'}
                                            <input type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={(e) => handleUploadReportImage(e, 'completion')} disabled={uploadingType} />
                                        </label>
                                    </div>
                                    {selectedQuery.technicianReport && (
                                        <div className="bg-gray-50 p-3 rounded-lg mb-2 text-sm text-gray-700 italic border border-gray-100">
                                            "{selectedQuery.technicianReport}"
                                        </div>
                                    )}
                                    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                                        {[...(selectedQuery.completionImages || []), ...(selectedQuery.adminCompletionImages || [])].map((img, idx) => {
                                            const url = getImageUri(img);
                                            const isPdf = url && url.toLowerCase().endsWith('.pdf');
                                            return url ? (
                                                <div key={idx} className="relative w-24 h-24 flex-shrink-0 group">
                                                    {isPdf ? (
                                                        <a href={url} target="_blank" rel="noreferrer" className="w-full h-full flex flex-col items-center justify-center bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
                                                            <svg className="w-6 h-6 text-red-500 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                                            <span className="text-xs text-red-600 font-medium">PDF</span>
                                                        </a>
                                                    ) : (
                                                        <a href={url} target="_blank" rel="noreferrer">
                                                            <img src={url} className="w-full h-full object-cover rounded-lg border border-gray-200" alt="Completion" />
                                                        </a>
                                                    )}
                                                    <button onClick={() => handleDeleteReportImage(url, 'completion')} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity transform hover:scale-110">
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                                    </button>
                                                </div>
                                            ) : null;
                                        })}
                                        {(!selectedQuery.completionImages?.length && !selectedQuery.adminCompletionImages?.length) && <p className="text-xs text-gray-400 py-2">No files available</p>}
                                    </div>
                                </div>
                            )}

                            {/* Invoices Component Removed - Merged into Payment & Invoice Section below */}

                            {/* Payment Summary */}
                            {/* Payment & Invoice Section - Only show after technician completion */}
                            {(getWorkflowProgress(selectedQuery.status) >= getWorkflowProgress(QUERY_STATUSES.TECHNICIAN_COMPLETED) || invoices.length > 0) && (
                            <div className="pt-4 border-t border-gray-100 pb-2">
                                <h3 className="text-sm font-bold text-gray-800 mb-2">Payment & Invoice</h3>
                                <div className="bg-gray-900 text-white rounded-2xl shadow-xl overflow-hidden">
                                    {/* Invoice Header */}
                                    <div className="p-5 flex justify-between items-center relative">
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-gray-800 rounded-full blur-3xl -mr-10 -mt-10 opacity-50"></div>
                                        <div className="relative z-10">
                                            {invoices.length > 0 ? (
                                                <div>
                                                    <p className="text-gray-400 text-xs mb-1 font-medium">Invoice #{invoices[0].invoiceNumber || 'INV'}</p>
                                                    {isEditingInvoiceAmount ? (
                                                        <div className="flex items-center gap-1">
                                                            <input type="number" step="0.01" min="0" className="w-24 px-2 py-0.5 text-sm bg-gray-700 border border-gray-600 rounded-md text-white outline-none" value={editInvoiceAmount} onChange={(e) => setEditInvoiceAmount(e.target.value)} placeholder={selectedQuery.amount || invoices[0].totalAmount || '0'} />
                                                            <button onClick={handleUpdateInvoiceAmount} className="bg-yellow-500 text-black hover:bg-yellow-400 px-2 py-0.5 rounded text-xs font-bold">Save</button>
                                                            <button onClick={() => setIsEditingInvoiceAmount(false)} className="text-gray-400 hover:text-white text-xs">Cancel</button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-2xl font-bold tracking-tight">{selectedQuery.amount || invoices[0].totalAmount || '0'} <span className="text-sm text-gray-500 font-normal">AED</span></span>
                                                            <button onClick={() => { setEditInvoiceAmount(selectedQuery.amount || invoices[0].totalAmount || ''); setIsEditingInvoiceAmount(true); }} className="text-gray-400 hover:text-yellow-400 transition-colors" title="Edit amount">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div>
                                                    <p className="text-gray-400 text-xs mb-1 font-medium">Total Amount</p>
                                                    {isEditingInvoiceAmount ? (
                                                        <div className="flex items-center gap-1">
                                                            <input type="number" step="0.01" min="0" className="w-24 px-2 py-0.5 text-sm bg-gray-700 border border-gray-600 rounded-md text-white outline-none" value={editInvoiceAmount} onChange={(e) => setEditInvoiceAmount(e.target.value)} placeholder={selectedQuery.amount || '0'} />
                                                            <button onClick={handleUpdateInvoiceAmount} className="bg-yellow-500 text-black hover:bg-yellow-400 px-2 py-0.5 rounded text-xs font-bold">Save</button>
                                                            <button onClick={() => setIsEditingInvoiceAmount(false)} className="text-gray-400 hover:text-white text-xs">Cancel</button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-2xl font-bold tracking-tight">{selectedQuery.amount || '0'} <span className="text-sm text-gray-500 font-normal">AED</span></span>
                                                            <button onClick={() => { setEditInvoiceAmount(selectedQuery.amount || ''); setIsEditingInvoiceAmount(true); }} className="text-gray-400 hover:text-yellow-400 transition-colors" title="Edit amount">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="relative z-10 flex flex-col items-end gap-1">
                                            <div className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold border ${selectedQuery.status?.toLowerCase().includes('paid') ? 'bg-green-500/10 text-green-400 border-green-500/30' : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'}`}>
                                                {selectedQuery.status?.toLowerCase().includes('paid') ? 'PAID' : 'PENDING'}
                                            </div>
                                            <button
                                                className="text-xs text-brand-golden hover:text-white underline mt-1"
                                            >
                                                {invoices.length} Invoices
                                            </button>
                                        </div>
                                    </div>

                                    {/* Actions & Proof */}
                                    <div className="bg-gray-800 p-4 border-t border-gray-700">

                                        {/* Upload Invoice Action */}
                                        <div className="flex justify-between items-center mb-4">
                                            <h4 className="text-xs font-bold text-gray-400 uppercase">Actions</h4>
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center gap-1 bg-gray-700/50 rounded-lg px-2 py-1.5">
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        placeholder="Amount"
                                                        value={invoiceAmount}
                                                        onChange={(e) => setInvoiceAmount(e.target.value)}
                                                        className="w-24 bg-transparent text-white text-xs border-0 outline-none placeholder-gray-500"
                                                    />
                                                    <span className="text-gray-400 text-xs">AED</span>
                                                </div>
                                                <label className="bg-yellow-500 text-black hover:bg-yellow-400 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition-colors shadow-sm flex items-center gap-1">
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                                                    {uploadingType === 'invoice' ? 'Uploading...' : 'Upload Invoice'}
                                                    <input type="file" multiple accept="application/pdf,image/*" className="hidden" onChange={handleUploadInvoice} disabled={uploadingType} />
                                                </label>
                                            </div>
                                        </div>

                                        {/* Invoice List */}
                                        {invoices.length > 0 && (
                                            <div className="space-y-2 mb-4">
                                                {invoices.map((inv) => (
                                                    <div key={inv.id} className="flex items-center justify-between p-2 bg-gray-700/50 rounded-lg border border-gray-700">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 bg-gray-600 rounded flex items-center justify-center text-gray-300">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                                            </div>
                                                            <div className="overflow-hidden">
                                                                <p className="text-xs font-bold text-gray-200 truncate max-w-[150px]">{inv.fileName}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-2">
                                                            <a href={inv.fileUrl} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg></a>
                                                            <button onClick={() => handleDeleteInvoice(inv.id)} className="text-red-400 hover:text-red-300"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Payment Receipt & Verification */}
                                        <div className="mt-4 pt-4 border-t border-gray-700">
                                            <div className="flex justify-between items-center mb-2">
                                                <p className="text-gray-400 text-xs font-bold uppercase">Payment Receipt</p>
                                                {!selectedQuery.paymentProof && (
                                                    <label className="bg-brand-golden hover:bg-yellow-400 text-black px-3 py-1 rounded-lg text-xs font-bold cursor-pointer flex items-center gap-1">
                                                        {uploadingType === 'payment' ? 'Uploading...' : (
                                                            <>
                                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                                                Upload Receipt
                                                            </>
                                                        )}
                                                        <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleAdminUploadPaymentProof} disabled={uploadingType} />
                                                    </label>
                                                )}
                                            </div>

                                            {selectedQuery.paymentProof ? (
                                                <div className="space-y-3">
                                                    <div className="bg-gray-700 p-2 rounded-lg inline-block">
                                                        <a href={getImageUri(selectedQuery.paymentProof)} target="_blank" rel="noreferrer">
                                                            <img
                                                                src={getImageUri(selectedQuery.paymentProof)}
                                                                alt="Payment Receipt"
                                                                className="h-32 w-auto object-cover rounded border border-gray-600"
                                                            />
                                                        </a>
                                                    </div>

                                                    {/* Verification Buttons */}
                                                    {(selectedQuery.status === QUERY_STATUSES.PAYMENT_UPLOADED || selectedQuery.status === 'Payment Uploaded') && (
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={handleVerifyPayment}
                                                                className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 rounded-lg font-bold text-sm transition-colors flex items-center justify-center gap-1"
                                                                disabled={isUpdating}
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                                                Accept Payment
                                                            </button>
                                                            <button
                                                                onClick={handleRejectPayment}
                                                                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg font-bold text-sm transition-colors flex items-center justify-center gap-1"
                                                                disabled={isUpdating}
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                                                Reject
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="text-gray-500 text-xs italic">
                                                    No payment receipt uploaded yet. Upload the proof received from the partner.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            )}

                            {/* After Service Section (Follow Up) */}
                            {(selectedQuery.followUp || getWorkflowProgress(selectedQuery.status) >= 100) && (
                                <div className="mt-6 pt-6 border-t border-gray-100">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-lg font-bold text-gray-900">After Service</h3>
                                        {selectedQuery.followUp?.status === 'resolved' && (
                                            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold">Resolved</span>
                                        )}
                                    </div>

                                    {selectedQuery.followUp ? (
                                        <div className="bg-orange-50 p-4 rounded-xl border border-orange-100">
                                            <div className="flex justify-between items-center mb-2">
                                                <h4 className="text-sm font-bold text-orange-900">Reported Issue</h4>
                                                <span className="text-xs text-gray-500">{formatDate(selectedQuery.followUp.createdAt)}</span>
                                            </div>
                                            <p className="text-sm text-gray-800 mb-3">{selectedQuery.followUp.description}</p>

                                            {selectedQuery.followUp.images && selectedQuery.followUp.images.length > 0 && (
                                                <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
                                                    {selectedQuery.followUp.images.map((img, idx) => (
                                                        <a key={idx} href={img} target="_blank" rel="noreferrer">
                                                            <img src={img} alt="Issue" className="w-16 h-16 object-cover rounded border border-orange-200" />
                                                        </a>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Admin Reply Section */}
                                            {selectedQuery.followUp.adminReply ? (
                                                <div className="mt-3 pt-3 border-t border-orange-200">
                                                    <p className="text-xs font-bold text-orange-900 mb-1">Admin Reply</p>
                                                    <p className="text-sm text-gray-700">{selectedQuery.followUp.adminReply}</p>
                                                    {selectedQuery.followUp.replyAt && (
                                                        <p className="text-xs text-gray-400 mt-1">{formatDate(selectedQuery.followUp.replyAt)}</p>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="mt-3 pt-3 border-t border-orange-200">
                                                    {isReplying ? (
                                                        <div className="space-y-2">
                                                            <textarea
                                                                className="w-full p-2 border border-orange-300 rounded-md text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                                                                rows="3"
                                                                placeholder="Write your reply..."
                                                                value={adminReply}
                                                                onChange={(e) => setAdminReply(e.target.value)}
                                                            ></textarea>
                                                            <div className="flex justify-end gap-2">
                                                                <button
                                                                    onClick={() => setIsReplying(false)}
                                                                    className="px-3 py-1 text-xs font-bold text-gray-600 hover:bg-gray-100 rounded"
                                                                >
                                                                    Cancel
                                                                </button>
                                                                <button
                                                                    onClick={handleReplyFollowUp}
                                                                    className="px-3 py-1 text-xs font-bold text-white bg-orange-600 hover:bg-orange-700 rounded"
                                                                    disabled={isUpdating || !adminReply.trim()}
                                                                >
                                                                    Send Reply
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => setIsReplying(true)}
                                                            className="text-xs font-bold text-orange-600 hover:text-orange-800 underline"
                                                        >
                                                            Reply to Issue
                                                        </button>
                                                    )}
                                                </div>
                                            )}

                                            {/* Resolve Information or Action */}
                                            {selectedQuery.followUp.status !== 'resolved' && (
                                                <div className="mt-4 flex justify-end">
                                                    <button
                                                        onClick={handleResolveFollowUp}
                                                        className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-xs font-bold rounded-lg shadow-sm transition-colors"
                                                        disabled={isUpdating}
                                                    >
                                                        Mark as Resolved
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="text-center py-6 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                            <p className="text-sm text-gray-400">No issues reported yet.</p>
                                        </div>
                                    )}
                                </div>
                            )}

                        </div>
                    </div>

                </div>
            )}
        </div>
    );
}

// Helper Component for Editing Partner/Manager
const PartnerManagerEditor = ({ partners, managers, currentPartnerId, currentManagerId, onSave, onCancel }) => {
    const [pId, setPId] = useState(currentPartnerId || '');
    const [mId, setMId] = useState(currentManagerId || '');

    return (
        <div className="flex flex-col gap-2 mt-1 bg-gray-50 p-2 rounded border border-gray-200">
            <select
                className="p-1 text-sm border rounded w-full"
                value={pId}
                onChange={(e) => setPId(e.target.value)}
            >
                <option value="">Select Partner</option>
                {partners.map(p => (
                    <option key={p.id} value={p.id}>{p.company || p.name}</option>
                ))}
            </select>

            <select
                className="p-1 text-sm border rounded w-full"
                value={mId}
                onChange={(e) => setMId(e.target.value)}
            >
                <option value="">Select Manager (Optional)</option>
                {managers.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                ))}
            </select>

            <div className="flex gap-2 justify-end">
                <button
                    onClick={onCancel}
                    className="text-white bg-gray-400 px-2 py-1 rounded text-xs hover:bg-gray-500"
                >
                    Cancel
                </button>
                <button
                    onClick={() => onSave(pId, mId)}
                    className="text-white bg-blue-600 px-2 py-1 rounded text-xs hover:bg-blue-700"
                    disabled={!pId}
                >
                    Save
                </button>
            </div>
        </div>
    );
};

export default Queries;
